import { ACTION_ITEM_STATUS, ACTION_ITEM_STATUS_META } from './constant';
import type { ActionItemAuditTrailEntry } from './models';

export interface AuditTrailRowContent {
  label: string;
  /** Who performed the step — rendered next to the timestamp. Undefined when unknown. */
  actor?: string;
  /** Status entries only: the note written when the status was set. */
  comment?: string;
}

const STATUS_LABEL: Record<string, string> = {
  [ACTION_ITEM_STATUS.TO_DO]: 'Action item created',
  [ACTION_ITEM_STATUS.IN_PROGRESS]: `Status updated to ${ACTION_ITEM_STATUS_META[ACTION_ITEM_STATUS.IN_PROGRESS].label}`,
  [ACTION_ITEM_STATUS.COMPLETED]: 'Marked as completed',
};

/**
 * D-FINAL: the Completed status entry's note is surfaced as its own "Final
 * comment" section on the detail pages, not the stepper's "Note:" line —
 * moved, not duplicated.
 *
 * Not identified by matching `entry.text` against the literal string
 * "Completed": the mock's own `PATCH /status` handler writes `text: 'Status
 * changed to Completed'`, not the bare enum value, so a literal comparison
 * never matches real seeded data. Completed is terminal — no further status
 * transition happens once an item is Completed — so the entry that put the
 * item in that state is always the LATEST `status`-type entry once the
 * item's current `status` is Completed.
 */
export const findCompletedEntry = (
  auditTrail: ActionItemAuditTrailEntry[],
  status: ACTION_ITEM_STATUS,
): ActionItemAuditTrailEntry | undefined => {
  if (status !== ACTION_ITEM_STATUS.COMPLETED) return undefined;

  return auditTrail
    .filter((entry) => entry.type === 'status')
    .reduce<ActionItemAuditTrailEntry | undefined>(
      (latest, entry) =>
        !latest || entry.timestamp > latest.timestamp ? entry : latest,
      undefined,
    );
};

/**
 * The API's status entries carry the acting user in `user` and a
 * human-readable sentence in `text` (observed: `'Status changed to
 * <label>'`, e.g. `'Status changed to Completed'` — NOT the bare enum value,
 * despite `STATUS_LABEL` below being keyed on the enum for a fallback that
 * rarely fires in practice); assignee entries carry the NEW assignee in
 * `user`. Turn each into what the timeline shows, always including WHO
 * performed the step (2026-09-04).
 */
export const describeAuditEntry = (
  entry: ActionItemAuditTrailEntry,
): AuditTrailRowContent => {
  const actor = entry.user?.name?.trim() || undefined;

  switch (entry.type) {
    case 'status':
      return {
        label: (entry.text && STATUS_LABEL[entry.text]) || entry.text || 'Status updated',
        actor,
        comment: entry.comment?.trim() || undefined,
      };
    case 'assigned':
      return { label: actor ? `Assigned to ${actor}` : 'Assigned' };
    case 'internalComment':
      return { label: actor ? `${actor} submitted a comment` : 'Comment submitted' };
    case 'fabricatorComment':
      return { label: actor ? `${actor} sent a comment` : 'Comment sent', actor };
    default:
      return { label: entry.text ?? entry.type, actor };
  }
};
